import {
  useCreateCallbackMutation,
  useUpdateCallbackMutation,
} from '../store/modules/merchant/api';
import { CallbackResponse, CallbackType } from '../store/modules/merchant/types';
import { isServerError } from '../util/helpers';

export type CallbackToUpsert = {
  callbackType: CallbackType;
  url: string;
  callbackId?: string;
  headers?: Record<string, string>;
} & Partial<CallbackResponse>;

/** Create or update a callback for the current merchant; inherited rows create a child override. */
const useCallbackUpsert = (merchantId: string) => {
  const [createCallback, { isLoading: isCreating }] = useCreateCallbackMutation();
  const [updateCallback, { isLoading: isUpdating }] = useUpdateCallbackMutation();

  const upsertOneCallback = async (callback: CallbackToUpsert) => {
    const {
      callbackType,
      url,
      callbackId,
      headers,
      merchantId: callbackMerchantId,
      isInherited,
    } = callback;
    const isInheritedCallback =
      Boolean(callbackId) &&
      (Boolean(isInherited) || (Boolean(callbackMerchantId) && callbackMerchantId !== merchantId));

    if (callbackId && !isInheritedCallback) {
      await updateCallback({
        callbackId,
        merchantId,
        url,
        headers,
      }).unwrap();
    } else {
      await createCallback({
        merchantId,
        callbackType,
        url,
        headers,
      }).unwrap();
    }
  };

  const onCallbackUpsert = async (callback: CallbackToUpsert) => {
    try {
      await upsertOneCallback(callback);
    } catch (err) {
      if (isServerError(err) && err.data.statusCode === 400) {
        throw new Error(err.data.message);
      }
      throw err;
    }
  };

  const onCallbackUpsertBatch = async (callbacks: CallbackToUpsert[]) => {
    for (const callback of callbacks) {
      await onCallbackUpsert(callback);
    }
  };

  return {
    onCallbackUpsert,
    onCallbackUpsertBatch,
    isLoading: isCreating || isUpdating,
  };
};

export { useCallbackUpsert };
